import React, { useState, useMemo } from 'react';
import { fmt, MES_LABEL, esVenta, esCompra } from './constants';

const SF_MESES = ['2025-08','2025-09','2025-10'];

export default function IvaPanel({ movimientos }) {
  const [neto, setNeto] = useState('');

  /* IVA por mes (solo meses con factura oficial) */
  const porMes = useMemo(()=>{
    const map={};
    movimientos.forEach(m=>{
      const mes=m.fecha.slice(0,7);
      if (!map[mes]) map[mes]={mes,debito:0,credito:0,sfVenta:0};
      if (esVenta(m)){
        if (m.subTipo==='facturada') map[mes].debito  += m.monto_iva||0;
        else                         map[mes].sfVenta += m.monto_iva||0;
      }
      if (esCompra(m)) map[mes].credito += m.monto_iva||0;
    });
    return Object.values(map).sort((a,b)=>a.mes.localeCompare(b.mes)).map(r=>({
      ...r,
      neto: SF_MESES.includes(r.mes) ? null : r.debito - r.credito,
      esSF: SF_MESES.includes(r.mes),
    }));
  },[movimientos]);

  /* Totales globales (solo meses facturados) */
  const totales = useMemo(()=>{
    const fact = porMes.filter(r=>!r.esSF);
    return {
      debito:  fact.reduce((a,r)=>a+r.debito,0),
      credito: fact.reduce((a,r)=>a+r.credito,0),
      neto:    fact.reduce((a,r)=>a+(r.neto||0),0),
    };
  },[porMes]);

  /* Calculadora */
  const netoN    = parseFloat(neto)||0;
  const ivaCalc  = Math.round(netoN*0.19);
  const bruto    = Math.round(netoN+ivaCalc);

  return (
    <div>
      <h2 style={{fontSize:15,fontWeight:700,marginBottom:16}}>
        <i className="ti ti-percentage" style={{marginRight:8,color:'var(--blue)'}} />IVA / Formulario 29
      </h2>

      {/* Aviso meses sin factura */}
      <div style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:'var(--radius)',padding:'10px 14px',marginBottom:16,fontSize:12,color:'var(--amber)',display:'flex',gap:8,alignItems:'flex-start'}}>
        <i className="ti ti-alert-triangle" style={{fontSize:16,flexShrink:0,marginTop:1}} />
        <span><b>Ago–Oct 2025:</b> empresa no habilitada en SII. IVA de esos meses <b>no es declarable</b> en F29. Se muestra solo como referencia interna.</span>
      </div>

      {/* KPIs globales */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:10,marginBottom:20}}>
        {[
          {label:'IVA Débito (ventas fact.)',  value:totales.debito,  color:'#ef4444', icon:'ti-file-invoice',   hint:'IVA cobrado en facturas emitidas'},
          {label:'IVA Crédito (compras)',      value:totales.credito, color:'#10b981', icon:'ti-receipt-2',      hint:'IVA pagado en compras con factura'},
          {label:'IVA Neto acumulado',         value:totales.neto,    color:'#3b82f6', icon:'ti-building-bank',  hint:'Débito − Crédito (períodos facturados)'},
        ].map(({label,value,color,icon,hint})=>(
          <div key={label} style={{background:'var(--card)',border:'1px solid var(--border)',borderLeft:`3px solid ${color}`,borderRadius:'var(--radius)',padding:'1rem 1.1rem'}}>
            <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:7}}>
              <i className={`ti ${icon}`} style={{fontSize:15,color}} />
              <span style={{fontSize:11,color:'var(--text2)'}}>{label}</span>
            </div>
            <p style={{fontSize:20,fontWeight:700,color}}>{fmt(value)}</p>
            <p style={{fontSize:10,color:'var(--text3)',marginTop:3}}>{hint}</p>
          </div>
        ))}
      </div>

      {/* Tabla mensual F29 */}
      <div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:'var(--radius)',overflow:'hidden',marginBottom:20}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',fontWeight:600,fontSize:13,display:'flex',alignItems:'center',gap:8}}>
          <i className="ti ti-calendar-stats" style={{color:'var(--blue)'}} />
          Desglose mensual — Base F29
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'var(--bg2)'}}>
                {['Período','Tipo','IVA Débito (ventas)','IVA Crédito (compras)','IVA Neto','Situación'].map(h=>(
                  <th key={h} style={{padding:'9px 12px',textAlign:'left',fontWeight:600,fontSize:11,color:'var(--text2)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {porMes.map((r,i)=>{
                const neto = r.neto;
                const sit  = r.esSF ? 'Sin efecto (S/F)' : neto>0 ? 'Pagar SII' : neto<0 ? 'Crédito a favor' : 'Neutro';
                const sitColor = r.esSF ? 'var(--amber)' : neto>0 ? 'var(--red)' : neto<0 ? 'var(--green)' : 'var(--text3)';
                return (
                  <tr key={r.mes} style={{background:i%2===0?'var(--card)':'transparent',borderBottom:'1px solid var(--border)',opacity:r.esSF?0.75:1}}>
                    <td style={{padding:'9px 12px',fontWeight:600,whiteSpace:'nowrap'}}>{MES_LABEL(r.mes)}</td>
                    <td style={{padding:'9px 12px'}}>
                      <span style={{fontSize:10,padding:'2px 7px',borderRadius:99,
                        background:r.esSF?'rgba(245,158,11,0.12)':'rgba(59,130,246,0.12)',
                        color:r.esSF?'var(--amber)':'var(--blue)',
                        border:`1px solid ${r.esSF?'rgba(245,158,11,0.3)':'rgba(59,130,246,0.3)'}`,
                      }}>{r.esSF?'Interno (S/F)':'Facturado'}</span>
                    </td>
                    <td style={{padding:'9px 12px',textAlign:'right',color:'#ef4444',fontWeight:600}}>{fmt(r.debito)}</td>
                    <td style={{padding:'9px 12px',textAlign:'right',color:'#10b981',fontWeight:600}}>{fmt(r.credito)}</td>
                    <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:r.esSF?'var(--text3)':neto>0?'var(--red)':neto<0?'var(--green)':'var(--text2)'}}>
                      {r.esSF ? '—' : fmt(neto)}
                    </td>
                    <td style={{padding:'9px 12px'}}>
                      <span style={{fontSize:11,fontWeight:500,color:sitColor}}>{sit}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{background:'var(--bg2)',borderTop:'2px solid var(--border)'}}>
                <td colSpan={2} style={{padding:'9px 12px',fontWeight:700}}>TOTAL (períodos facturados)</td>
                <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:'#ef4444'}}>{fmt(totales.debito)}</td>
                <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:'#10b981'}}>{fmt(totales.credito)}</td>
                <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:totales.neto>0?'var(--red)':'var(--green)'}}>{fmt(totales.neto)}</td>
                <td style={{padding:'9px 12px',fontWeight:700,color:totales.neto>0?'var(--red)':'var(--green)'}}>{totales.neto>0?'Por pagar SII':'Crédito a favor'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Calculadora */}
      <div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'1.25rem'}}>
        <p style={{fontWeight:600,fontSize:13,marginBottom:12}}>
          <i className="ti ti-calculator" style={{marginRight:7,color:'var(--amber)'}} />Calculadora IVA 19%
        </p>
        <div style={{display:'flex',gap:16,alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:160}}>
            <label style={{fontSize:11,color:'var(--text2)',display:'block',marginBottom:5}}>Monto neto (sin IVA)</label>
            <input type="number" min="0" placeholder="0" value={neto} onChange={e=>setNeto(e.target.value)} />
          </div>
          <div style={{display:'flex',gap:24,paddingBottom:10,flexWrap:'wrap'}}>
            <div>
              <p style={{fontSize:11,color:'var(--text3)',marginBottom:3}}>IVA (19%)</p>
              <p style={{fontSize:20,fontWeight:700,color:'var(--blue)'}}>{new Intl.NumberFormat('es-CL').format(ivaCalc)}</p>
            </div>
            <div>
              <p style={{fontSize:11,color:'var(--text3)',marginBottom:3}}>Total bruto</p>
              <p style={{fontSize:20,fontWeight:700,color:'var(--text)'}}>{new Intl.NumberFormat('es-CL').format(bruto)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
